import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { HbrButton, HbrDomTable } from '@harbor/elements-react-standalone';
import { RootState } from '../../../support/store';
import {
  Column,
  ColumnAlignment,
  ColumnType,
  DataType,
  getColumnTypeConfig,
} from '../../../types/table';
import { OrgChartRow, getDirectReports } from '../../../support/orgScope';
import {
  DELIVERY_STATUSES,
  type CampaignEnrollment,
  type DeliveryStatus,
} from '../../../support/campaigns';
import { formatPipelineUsd, type DeliveryMetrics } from '../../../support/campaignLeader';
import {
  EnablementCell,
  directReportEnablement,
  icJourneyMaps,
  managerUsersCompleted,
  reportIsIncomplete,
} from '../../../support/campaignEnrollmentLeader';
import type { CompletionCohort } from '../../../support/campaignSalesLevels';
import {
  ReminderReportLine,
  buildIcReminderCard,
  buildManagerReminderCard,
  icReminderText,
  managerReminderText,
} from '../../../support/campaignEnablementNotify';
import {
  ReminderOutcome,
  classifyReminderOrder,
  reminderErrorFromOrder,
  useEnablementReminderOrderStatus,
  useSubmitEnablementReminder,
} from '../../../hooks/campaign-hooks';

/**
 * Team rollup for the Leader Campaigns tab. One row per direct report of the selected manager; each
 * row's delivery numbers are that person's whole org subtree rolled up (their own deliveries plus every
 * descendant's, supplied by `deliveryFor`), so a manager sees their sub-managers' totals aggregated and an IC
 * row is just that IC's own deliveries. Clicking a name drills into that person (a manager opens their
 * team, an IC opens their individual page), exactly like the Salesforce `TeamMembersTable`.
 *
 * Each row also carries an **enablement completion** cell and a **Notify** button:
 *   • an IC row reads "X of Y" journey maps complete (their own enrollment rollup); Notify reminds that
 *     IC of the maps they still owe;
 *   • a manager (or manager-of-managers) row reads "X of Y" users complete — every individual contributor
 *     under them, descending through sub-managers; Notify reminds the manager of the reports still out.
 * The reminder is a Webex Adaptive Card sent to the row's own person via the toolbox_queue app.
 */

interface Props {
  orgRows: OrgChartRow[];
  managerEmail: string;
  /** A person's team delivery figures (themselves plus everyone under them) and pipeline $. */
  deliveryFor: (email: string) => { metrics: DeliveryMetrics; pipelineTotal: number };
  /** Enrollments bucketed by lowercased user email (raw rollup — carries per-map counts). */
  byUser: Map<string, CampaignEnrollment[]>;
  /** Per-owner completion cohorts by lowercased email (scalable fallback when raw rows aren't loaded). */
  cohortsByEmail: Map<string, CompletionCohort>;
  /** Human campaign name for the reminder card; defaults to "this campaign" when unknown. */
  campaignName?: string;
  onSelectEmail: (email: string) => void;
}

type RowModel = {
  id: string;
  email: string;
  is_manager: boolean;
  total: number;
  active: number;
  /** Subtree pipeline $, pre-formatted (compact USD). */
  pipeline: string;
  /** "X of Y" enablement completion (journey maps for an IC, users for a manager). */
  enablement: string;
} & Record<DeliveryStatus, number>;

/** Per-row enablement detail kept aside for the Notify handler (not shown as table data). */
interface RowMeta {
  isManager: boolean;
  cell: EnablementCell;
  /**
   * For a manager row: the outstanding DIRECT reports, each with its own role-appropriate count
   * (a sub-manager's team "X of Y users", an IC's "X of Y journey maps") — the reminder card's lines.
   * Empty for an IC row (an IC's reminder is their own map count, not a breakdown).
   */
  reportLines: ReminderReportLine[];
}

function safeIdFromEmail(email: string) {
  return `u_${email.toLowerCase().replace(/[^a-z0-9_-]/g, '_')}`;
}

export default function CampaignsTeamMembersTable({
  orgRows,
  managerEmail,
  deliveryFor,
  byUser,
  cohortsByEmail,
  campaignName,
  onSelectEmail,
}: Props) {
  const normalizedManagerEmail = String(managerEmail || '').toLowerCase();
  const campaignLabel = campaignName || 'this campaign';

  // Who is logged in — Notify is a scoped action, permitted only on the current user's OWN direct
  // reports (and their own row), never on reports-of-reports. The table re-roots as you drill into a
  // sub-manager, so the viewed `managerEmail` is NOT the right anchor for the permission check.
  const currentUserEmail = useSelector((state: RootState) =>
    String(state.user.userData?.email || '').toLowerCase(),
  );
  // The set the current user may remind: their own direct reports plus themselves. Membership here is a
  // permission gate, independent of which manager's team is currently being viewed.
  const notifiableEmails = useMemo(() => {
    const set = new Set<string>();
    if (currentUserEmail) {
      set.add(currentUserEmail);
      for (const r of getDirectReports(orgRows, currentUserEmail)) {
        set.add(String(r.user_email || '').toLowerCase());
      }
    }
    return set;
  }, [orgRows, currentUserEmail]);

  const notify = useSubmitEnablementReminder();
  // One reminder is in flight at a time. `active` tracks whose it is + the order to poll (null orderId
  // until the enqueue returns); `outcomeByEmail` records the true outcome once the order goes terminal;
  // `errorByEmail` keeps the failure reason for the button tooltip.
  const [active, setActive] = useState<{ email: string; orderId: string | null } | null>(null);
  const [outcomeByEmail, setOutcomeByEmail] = useState<Map<string, ReminderOutcome>>(() => new Map());
  const [errorByEmail, setErrorByEmail] = useState<Map<string, string>>(() => new Map());

  const { data: polledOrder } = useEnablementReminderOrderStatus(active?.orderId);

  // When the polled order reaches a terminal state, resolve the button to the real outcome. The worker
  // reports both a dev/allow-list skip AND a masked Webex send error as task success, so
  // classifyReminderOrder distinguishes sent/skipped/failed and reminderErrorFromOrder pulls the reason.
  useEffect(() => {
    if (!active?.orderId) return;
    const outcome = classifyReminderOrder(polledOrder);
    if (!outcome) return;
    const email = active.email;
    setOutcomeByEmail((prev) => new Map(prev).set(email, outcome));
    if (outcome === 'failed') {
      const reason = reminderErrorFromOrder(polledOrder) || 'The Webex message could not be sent.';
      setErrorByEmail((prev) => new Map(prev).set(email, reason));
    }
    setActive(null);
  }, [polledOrder, active]);

  const reports = useMemo(
    () => getDirectReports(orgRows, normalizedManagerEmail),
    [orgRows, normalizedManagerEmail],
  );

  // Row data + the per-row enablement meta (kept in a side map so the Notify handler has the card inputs).
  const { data, metaByEmail } = useMemo(() => {
    const metaByEmail = new Map<string, RowMeta>();
    const data: RowModel[] = reports.map((r) => {
      const email = String(r.user_email || '').toLowerCase();
      const isManager = !!r.is_manager;
      const { metrics, pipelineTotal } = deliveryFor(email);

      let cell: EnablementCell;
      let reportLines: ReminderReportLine[] = [];
      if (isManager) {
        cell = managerUsersCompleted(email, orgRows, byUser, cohortsByEmail).cell;
        // Break the reminder down by each outstanding DIRECT report: a sub-manager reads their team's
        // "X of Y users", an IC reads their own "X of Y journey maps".
        reportLines = directReportEnablement(email, orgRows, byUser, cohortsByEmail)
          .filter(reportIsIncomplete)
          .map((r) => ({
            email: r.email,
            done: r.cell.done,
            total: r.cell.total,
            unit: r.cell.kind === 'users' ? 'users' : 'journey maps',
          }));
      } else {
        cell = icJourneyMaps(email, byUser, cohortsByEmail);
      }
      metaByEmail.set(email, { isManager, cell, reportLines });

      const row = {
        id: safeIdFromEmail(email),
        email,
        is_manager: isManager,
        total: metrics.total,
        active: metrics.active,
        pipeline: formatPipelineUsd(pipelineTotal),
        enablement: `${cell.done} of ${cell.total}`,
      } as RowModel;
      for (const s of DELIVERY_STATUSES) row[s.value] = metrics.byStatus[s.value];
      return row;
    });
    return { data, metaByEmail };
  }, [reports, orgRows, deliveryFor, byUser, cohortsByEmail]);

  // Whether a row's Notify button does anything. Two gates:
  //   1. PERMISSION — the row must be one the current user may remind: their own direct report or their
  //      own row (`notifiableEmails`). Reports-of-reports (rows shown after drilling into a sub-manager)
  //      are never notifiable by this viewer — that sub-manager reminds their own team.
  //   2. OUTSTANDING WORK — there is enablement left to finish (`cell.done < cell.total`). This uses the
  //      same aggregate figure shown in the Enablement column for BOTH roles, so a manager row is no
  //      longer gated on the per-report breakdown (which is empty in cohort mode → previously disabled
  //      every manager row).
  // Stays enabled after a send/failure so the leader can re-notify or retry (a self-reminder is routed
  // through the bot token in useSubmitEnablementReminder, so it delivers rather than 400ing).
  const canNotify = (email: string): boolean => {
    if (!notifiableEmails.has(email)) return false;
    const meta = metaByEmail.get(email);
    if (!meta) return false;
    if (meta.cell.total <= 0) return false;
    return meta.cell.done < meta.cell.total;
  };

  const handleNotify = async (email: string) => {
    const meta = metaByEmail.get(email);
    if (!meta || active) return; // one reminder in flight at a time
    const { cell, reportLines, isManager } = meta;
    // Deep-link back to the recipient's Leader dashboard on this branch's UI origin (never a build-time
    // override, so one bundle links correctly across dev/uat/prod — same rule as the SSO redirect URI).
    const leaderUrl =
      typeof window !== 'undefined' ? `${window.location.origin}/leader` : undefined;
    const card = isManager
      ? buildManagerReminderCard({ campaignName: campaignLabel, done: cell.done, total: cell.total, reports: reportLines, leaderUrl })
      : buildIcReminderCard({ campaignName: campaignLabel, done: cell.done, total: cell.total, leaderUrl });
    const messageText = isManager
      ? managerReminderText({ campaignName: campaignLabel, done: cell.done, total: cell.total, reports: reportLines, leaderUrl })
      : icReminderText({ campaignName: campaignLabel, done: cell.done, total: cell.total, leaderUrl });

    // Clear any prior outcome for this row and mark it in flight (orderId filled once the enqueue returns).
    setOutcomeByEmail((prev) => {
      if (!prev.has(email)) return prev;
      const next = new Map(prev);
      next.delete(email);
      return next;
    });
    setActive({ email, orderId: null });
    try {
      const order = await notify.mutateAsync({ recipientEmail: email, messageText, card });
      if (order?.order_id) {
        // Keep it in flight; the poll hook + effect resolve the true Sent/Skipped/Failed outcome.
        setActive({ email, orderId: order.order_id });
      } else {
        setActive(null); // nothing was submitted (no recipient)
      }
    } catch {
      setOutcomeByEmail((prev) => new Map(prev).set(email, 'failed'));
      setActive(null);
    }
  };

  const visibleColumns: Column[] = useMemo(() => {
    // Percentage widths (summing to 100%) rather than fixed px: HbrDomTable's table is width:100%, so
    // percent columns shrink/grow WITH the container and never force horizontal scroll. Fixed px widths
    // become hard min-widths (see column.service setWidth) whose sum overflowed the screen.
    const num = (id: string, name: string, width = '8%'): Column => ({
      id,
      name,
      width,
      columnType: ColumnType.Number,
      sortable: false,
      align: ColumnAlignment.Right,
      dataType: DataType.Number,
      resizable: false,
      reorderable: false,
    });
    const custom = (id: string, name: string, width: string, align: ColumnAlignment): Column => ({
      id,
      name,
      width,
      columnType: ColumnType.Custom,
      sortable: false,
      align,
      dataType: DataType.String,
      resizable: false,
      reorderable: false,
    });
    const base: Column[] = [
      custom('email', 'Team Member', '20%', ColumnAlignment.Left),
      num('total', 'Total', '7%'),
      num('active', 'Active', '7%'),
      custom('pipeline', 'Pipeline $', '9%', ColumnAlignment.Right),
      custom('enablement', 'Enablement', '9%', ColumnAlignment.Right),
      ...DELIVERY_STATUSES.map((s) => num(s.value, s.label, '8%')),
      custom('notify', 'Notify', '8%', ColumnAlignment.Center),
    ];

    return base
      .map((c) => getColumnTypeConfig(c))
      .map((c) => ({ ...c, resizable: false, reorderable: false }));
  }, []);

  const renderHeader = () =>
    // No `w-max` here: that forced each header to its single-line max-content width, expanding the
    // column past its percentage share. Let the label wrap/ellipsize within the allotted width instead.
    visibleColumns.map((column) => (
      <div key={`column-${column.id}`} slot={`column-${column.id}`}>
        <div
          className="flex items-center relative"
          style={{
            justifyContent:
              column.align === ColumnAlignment.Right
                ? 'flex-end'
                : column.align === ColumnAlignment.Center
                ? 'center'
                : 'flex-start',
          }}
        >
          <div>{column.name}</div>
        </div>
      </div>
    ));

  const renderBody = () => {
    if (!data.length) return null;
    const list: JSX.Element[] = [];

    for (const row of data) {
      for (const column of visibleColumns) {
        const slot = `row-${row.id}-column-${column.id}`;

        if (column.id === 'email') {
          list.push(
            <div
              key={slot}
              slot={slot}
              className="min-h-8 min-w-0 flex items-center font-semibold hover:text-link hover:underline cursor-pointer truncate"
              title={`${row.email}${row.is_manager ? ' (Mgr)' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelectEmail(row.email);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectEmail(row.email);
                }
              }}
              aria-label={`View ${row.email}`}
            >
              {row.email}
              {row.is_manager ? ' (Mgr)' : ''}
            </div>,
          );
          continue;
        }

        if (column.id === 'notify') {
          const isPending = active?.email === row.email;
          const outcome = outcomeByEmail.get(row.email);
          // Only the true outcome shows a settled label; a failure/skip stays retryable.
          const label = isPending
            ? 'Sending…'
            : outcome === 'sent'
              ? 'Notified'
              : outcome === 'skipped'
                ? 'Skipped'
                : outcome === 'failed'
                  ? 'Retry'
                  : 'Notify';
          const enabled = canNotify(row.email) && !active;
          const title =
            outcome === 'failed'
              ? errorByEmail.get(row.email) || 'The Webex message could not be sent.'
              : outcome === 'skipped'
                ? 'No Webex message was delivered — retry when Webex is connected.'
                : outcome === 'sent'
                  ? 'Reminder sent — click to send again.'
                  : undefined;
          list.push(
            <div key={slot} slot={slot} className="min-h-8 flex items-center justify-center box-content">
              <HbrButton
                variant="outline"
                size="small"
                disabled={!enabled}
                aria={{ label: `Notify ${row.email}` }}
                title={title}
                onClick={(evt: any) => {
                  evt.stopPropagation();
                  handleNotify(row.email);
                }}
              >
                {label}
              </HbrButton>
            </div>,
          );
          continue;
        }

        if (column.id === 'enablement') {
          list.push(
            <div
              key={slot}
              slot={slot}
              className="min-h-8 flex items-center box-content"
              style={{ justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}
              title={row.is_manager ? 'Users completed of team ICs' : 'Journey maps completed of assigned'}
            >
              {row.enablement}
            </div>,
          );
          continue;
        }

        const value = (row as any)[column.id];
        list.push(
          <div
            key={slot}
            slot={slot}
            className="min-h-8 flex items-center box-content"
            style={{ justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}
          >
            {value ?? 0}
          </div>,
        );
      }
    }

    return list;
  };

  if (!reports.length) {
    return <div className="hbr-css__text-secondary-sm">No direct reports found.</div>;
  }

  return (
    <HbrDomTable
      autoLayout={true}
      columns={visibleColumns}
      data={data}
      style={{ display: 'block', width: '100%' }}
    >
      {renderHeader()}
      {renderBody()}
    </HbrDomTable>
  );
}
